import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import { fmtDate, toInputDate } from '../../utils';

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ title, onClose, children, size = '' }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal${size ? ` modal-${size}` : ''}`}>
        <div className="modal-header">
          <span style={{ fontWeight:600,fontSize:15 }}>{title}</span>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:20,cursor:'pointer',color:'var(--gray-400)',lineHeight:1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function Badge({ status }) {
  const map = { active:'success',won:'success',strong:'success',yes:'success',draft:'warning','pre-rfp':'warning',sent:'warning','under_review':'warning',high:'warning',critical:'danger',lost:'danger',expired:'danger',cancelled:'danger',inactive:'danger',no:'danger',medium:'info',proposal:'info',qualification:'info',shaping:'info',prospecting:'default',evaluation:'default','on hold':'default','no bid':'default',low:'default',none:'default',prime:'default',federal:'default',commercial:'default','t&m':'default','ffp':'default' };
  const k = (status||'').toLowerCase();
  const type = map[k] || 'default';
  return <span className={`badge badge-${type}`}>{status||'—'}</span>;
}

// ── Money ─────────────────────────────────────────────────────────────────────
export function Money({ value }) {
  const n = Number(value);
  if (!value && value !== 0 || isNaN(n)) return <span className="text-muted">—</span>;
  if (n >= 1e9) return <span>${(n/1e9).toFixed(1).replace(/\.0$/,'')}B</span>;
  if (n >= 1e6) return <span>${(n/1e6).toFixed(1).replace(/\.0$/,'')}M</span>;
  if (n >= 1e3) return <span>${n.toLocaleString()}</span>;
  return <span>${n.toLocaleString()}</span>;
}

// ── Pagination ────────────────────────────────────────────────────────────────
export function Pagination({ page, limit, total, onPage }) {
  const pages = Math.ceil(total / limit);
  if (pages <= 1) return null;
  return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'12px 20px',borderTop:'1px solid var(--gray-100)' }}>
      <button className="btn btn-secondary btn-sm" onClick={() => onPage(page-1)} disabled={page===1}>← Prev</button>
      <span style={{ fontSize:13,color:'var(--gray-500)',padding:'0 8px' }}>Page {page} of {pages} · {total} total</span>
      <button className="btn btn-secondary btn-sm" onClick={() => onPage(page+1)} disabled={page>=pages}>Next →</button>
    </div>
  );
}

// ── ConfirmDialog ─────────────────────────────────────────────────────────────
export function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = 'Confirm', danger = true }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:400 }}>
        <div className="modal-header"><span style={{ fontWeight:600 }}>Confirm Action</span></div>
        <div className="modal-body"><p style={{ margin:0,color:'var(--gray-700)' }}>{message}</p></div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── ActivityFeed ──────────────────────────────────────────────────────────────
export function ActivityFeed({ relatedType, relatedId, activities: init = [], canContribute = true }) {
  const [activities, setActivities] = useState(init);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setActivities(init); }, [init]);

  const typeIcon = { note:'📝', call:'📞', email:'✉️', meeting:'🤝', created:'✨', stage_change:'🔄', status_change:'🔄', mod_added:'📋', default:'💬' };


  const handleAdd = async () => {
    if (!subject.trim()) return;
    setSaving(true);
    try {
      const pluralMap = { opportunity:'opportunities', contract:'contracts', account:'accounts', contact:'contacts' };
      const endpoint = relatedType && relatedId
        ? `/${pluralMap[relatedType] || relatedType + 's'}/${relatedId}/activities`
        : '/activities';
      const r = await api.post(endpoint, { subject, body, type:'note', related_type:relatedType, related_id:relatedId });
      if (r && r.data) {
        setActivities(p => [r.data, ...p]);
        setSubject(''); setBody('');
      }
    } catch(err) {
      const status = err?.response?.status;
      if (status === 401) {
        alert('Your session has expired. Please refresh the page and log in again.');
      } else {
        alert('Failed to post update: ' + (err?.response?.data?.error || 'Unknown error'));
      }
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="card-header"><span className="card-title">Activity Feed</span></div>
      <div className="card-body">
        {canContribute && (
          <div style={{ marginBottom:16 }}>
            <input className="form-input" placeholder="Add a note or update..." value={subject} onChange={e => setSubject(e.target.value)} style={{ marginBottom:8 }} />
            <textarea className="form-textarea" placeholder="Details (optional)..." value={body} onChange={e => setBody(e.target.value)} rows={2} style={{ marginBottom:8 }} />
            <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving || !subject.trim()}>{saving ? 'Posting...' : 'Post Update'}</button>
          </div>
        )}
        <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
          {activities.length === 0 && <div className="empty-state" style={{ padding:'20px 0' }}><div className="empty-state-text">No activity yet</div></div>}
          {activities.map(a => (
            <div key={a.id} style={{ display:'flex',gap:10,padding:'10px 0',borderBottom:'1px solid var(--gray-100)' }}>
              <span style={{ fontSize:16,flexShrink:0,marginTop:1 }}>{typeIcon[a.type]||typeIcon.default}</span>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontWeight:500,fontSize:13,color:'var(--gray-800)' }}>{a.subject}</div>
                {a.body && <div style={{ fontSize:12,color:'var(--gray-600)',marginTop:2,whiteSpace:'pre-wrap' }}>{a.body}</div>}
                <div style={{ fontSize:11,color:'var(--gray-400)',marginTop:4 }}>
                  {a.user_name && <span>{a.user_name} · </span>}
                  {fmtDate(a.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── TaskList ──────────────────────────────────────────────────────────────────
export function TaskList({ relatedType, relatedId, tasks: init = [], users = [], canContribute = true, onUpdate }) {
  const [tasks, setTasks] = useState(init);
  const [showForm, setShowForm] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [form, setForm] = useState({ title:'', description:'', assigned_to:'', due_date:'', priority:'Medium' });
  const [saving, setSaving] = useState(false);

  const STATUSES   = ['Open','In Progress','Done','Cancelled'];
  const PRIORITIES = ['Critical','High','Medium','Low'];

  const createEndpoint =
    relatedType === 'opportunity' ? `/opportunities/${relatedId}/tasks` :
    relatedType === 'contract'    ? `/contracts/${relatedId}/tasks`    :
                                    `/accounts/${relatedId}/tasks`;

  const handleAdd = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const r = await api.post(createEndpoint, { ...form, due_date: form.due_date || null });
      setTasks(p => [...p, r.data]);
      setForm({ title:'', description:'', assigned_to:'', due_date:'', priority:'Medium' });
      setShowForm(false);
      if (onUpdate) onUpdate();
    } catch(e) { alert(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleEditSave = async () => {
    if (!editTask?.title?.trim()) return;
    setSaving(true);
    try {
      const r = await api.put(`/tasks/${editTask.id}`, {
        title:       editTask.title,
        description: editTask.description,
        notes:       editTask.notes,
        assigned_to: editTask.assigned_to || null,
        due_date:    editTask.due_date    || null,
        status:      editTask.status,
        priority:    editTask.priority,
      });
      setTasks(p => p.map(t => t.id === r.data.id ? r.data : t));
      setEditTask(null);
      if (onUpdate) onUpdate();
    } catch(e) { alert(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggleDone = async (task) => {
    const newStatus = task.status === 'Done' ? 'Open' : 'Done';
    const r = await api.put(`/tasks/${task.id}`, { status: newStatus });
    setTasks(p => p.map(t => t.id === task.id ? r.data : t));
  };

  const priorityColor = p => ({ Critical:'#dc2626', High:'#f97316', Medium:'#f59e0b', Low:'#16a34a' }[p] || '#94a3b8');
  const statusColor   = s => ({ Open:'#3b82f6','In Progress':'#f59e0b', Done:'#16a34a', Overdue:'#dc2626', Cancelled:'#94a3b8' }[s] || '#94a3b8');
  const openCount     = tasks.filter(t => !['Done','Cancelled'].includes(t.status)).length;

  return (
    <div>
      <div className="card-header">
        <span className="card-title">Tasks ({openCount} open)</span>
        {canContribute && (
          <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(!showForm)}>+ Add Task</button>
        )}
      </div>
      <div className="card-body">
        {/* Inline add form */}
        {showForm && (
          <div style={{ background:'var(--gray-50)',borderRadius:8,padding:14,marginBottom:14 }}>
            <div className="form-group">
              <input className="form-input" placeholder="Task title *" value={form.title}
                onChange={e => setForm(f => ({ ...f, title:e.target.value }))} autoFocus />
            </div>
            <div className="form-group">
              <textarea className="form-textarea" placeholder="Description (optional)" rows={2} value={form.description}
                onChange={e => setForm(f => ({ ...f, description:e.target.value }))} />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <select className="form-select" value={form.assigned_to}
                  onChange={e => setForm(f => ({ ...f, assigned_to:e.target.value }))}>
                  <option value="">Unassigned</option>
                  {users.filter(u => !u.email?.startsWith('admin@')).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <input className="form-input" type="date" value={form.due_date}
                  onChange={e => setForm(f => ({ ...f, due_date:e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <select className="form-select" value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority:e.target.value }))}>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ display:'flex',gap:8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving}>
                {saving ? 'Adding...' : 'Add Task'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Task list */}
        {tasks.length === 0 && (
          <div className="empty-state" style={{ padding:'16px 0' }}>
            <div className="empty-state-text">No tasks yet</div>
          </div>
        )}
        {tasks.map(t => (
          <div className="task-item" key={t.id}
            style={{ opacity:['Done','Cancelled'].includes(t.status) ? 0.55 : 1 }}>
            <div className={`task-check${t.status === 'Done' ? ' done' : ''}`}
              onClick={() => toggleDone(t)}>
              {t.status === 'Done' ? '✓' : ''}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div className={`task-title${t.status === 'Done' ? ' done' : ''}`}>{t.title}</div>
              <div className="task-meta">
                {t.assigned_to_name && <span>👤 {t.assigned_to_name}</span>}
                {t.due_date && <span style={{ marginLeft:8 }}>📅 {fmtDate(t.due_date)}</span>}
                <span style={{ marginLeft:8, color:priorityColor(t.priority), fontWeight:600 }}>
                  {t.priority}
                </span>
                <span style={{ marginLeft:8, color:statusColor(t.status), fontSize:11 }}>
                  {t.status}
                </span>
              </div>
            </div>
            {canContribute && (
              <button className="btn btn-secondary btn-sm btn-icon" title="Edit task"
                style={{ marginLeft:8, flexShrink:0 }}
                onClick={() => setEditTask({ ...t, due_date:toInputDate(t.due_date) })}>
                ✏️
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Edit task modal */}
      {editTask && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setEditTask(null)}>
          <div className="modal">
            <div className="modal-header">
              <span style={{ fontWeight:600,fontSize:15 }}>Edit Task</span>
              <button onClick={() => setEditTask(null)}
                style={{ background:'none',border:'none',fontSize:20,cursor:'pointer',color:'var(--gray-400)' }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-input" value={editTask.title} autoFocus
                  onChange={e => setEditTask(t => ({ ...t, title:e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" rows={2} value={editTask.description||''}
                  onChange={e => setEditTask(t => ({ ...t, description:e.target.value }))} />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={editTask.status}
                    onChange={e => setEditTask(t => ({ ...t, status:e.target.value }))}>
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-select" value={editTask.priority}
                    onChange={e => setEditTask(t => ({ ...t, priority:e.target.value }))}>
                    {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  <select className="form-select" value={editTask.assigned_to||''}
                    onChange={e => setEditTask(t => ({ ...t, assigned_to:e.target.value }))}>
                    <option value="">Unassigned</option>
                    {users.filter(u => !u.email?.startsWith('admin@')).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input className="form-input" type="date" value={editTask.due_date||''}
                    onChange={e => setEditTask(t => ({ ...t, due_date:e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={editTask.notes||''}
                  placeholder="Progress notes, blockers..."
                  onChange={e => setEditTask(t => ({ ...t, notes:e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditTask(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────
export function Toggle({ checked, onChange }) {
  return (
    <label className="toggle-switch">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="toggle-slider" />
    </label>
  );
}

// ── PursuitScore ──────────────────────────────────────────────────────────────
export function PursuitScore({ checklist }) {
  if (!checklist) return null;
  const fields = ['core_capabilities','customer_access','partner_path','vehicle_access','can_staff','margin_acceptable','past_performance','compliance_manageable'];
  const score = fields.filter(f => checklist[f] === true || checklist[f] === 1).length;
  const pct = Math.round((score/fields.length)*100);
  const color = pct >= 75 ? '#16a34a' : pct >= 50 ? '#f59e0b' : '#dc2626';
  return (
    <div style={{ display:'flex',alignItems:'center',gap:10 }}>
      <div style={{ flex:1,height:6,background:'var(--gray-200)',borderRadius:3 }}>
        <div style={{ width:`${pct}%`,height:'100%',background:color,borderRadius:3,transition:'width 0.3s' }} />
      </div>
      <span style={{ fontSize:12,fontWeight:600,color,minWidth:36 }}>{score}/{fields.length}</span>
    </div>
  );
}
